/*
 * cart-upsell — recommandations produits dans le tiroir panier.
 *
 * Remplace la campagne "upsell" de Corner Cart en s'appuyant sur les
 * recommandations natives Shopify (/recommendations/products.json), les memes
 * que Corner utilisait. Le contenu se pilote donc toujours depuis l'app
 * "Search & Discovery".
 *
 * Le tiroir natif re-rend tout .drawer__inner a chaque mise a jour du panier :
 * l'element est donc recree et connectedCallback rejoue tout seul. C'est ce qui
 * garantit que les produits deja au panier sortent des recommandations.
 */

class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rendered === 'true') return;
    this.dataset.rendered = 'true';
    this.render();
  }

  get root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  get money() {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    const locale = document.documentElement.lang || 'fr';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (error) {
      return new Intl.NumberFormat('fr', { style: 'currency', currency: 'EUR' });
    }
  }

  async render() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const limit = parseInt(this.dataset.limit || '10', 10);
    const max = parseInt(this.dataset.max || '4', 10);
    const liste = (valeur) => (valeur || '').split(',').map((id) => id.trim()).filter(Boolean);
    const inCart = liste(this.dataset.inCart);
    // Coffrets a ecarter quand le panier en contient deja un. Vide le reste du
    // temps : l'exclusion ne coute rien quand elle ne s'applique pas.
    const exclus = liste(this.dataset.excludeIds);
    // Variantes deja au panier SANS ligne propre : les fioles contenues dans un
    // coffret. Une suggestion dont une variante y figure est ecartee.
    const variantesDeja = liste(this.dataset.inCartVariants);
    const garder = (product) => product.available
      && !inCart.includes(String(product.id))
      && !exclus.includes(String(product.id))
      && !(product.variants || []).some((v) => variantesDeja.includes(String(v.id)));

    let products = [];

    try {
      const url = `${this.root}recommendations/products.json?product_id=${encodeURIComponent(
        productId
      )}&limit=${limit}&intent=related`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        products = (data.products || []).filter(garder).slice(0, max);
      }
    } catch (error) {
      // pas de return : les epices de secours peuvent encore remplir la liste
    }

    // Recommandations trop maigres une fois filtrees (panier avec un coffret,
    // dont les dix epices sont ecartees) : on complete avec les epices de
    // data-fallback, chargees une a une dans le meme format JSON.
    const secours = liste(this.dataset.fallback);
    for (const handle of secours) {
      if (products.length >= max) break;
      if (products.some((p) => p.handle === handle)) continue;
      try {
        const reponse = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`);
        if (!reponse.ok) continue;
        const produit = await reponse.json();
        if (garder(produit)) products.push(produit);
      } catch (error) {
        // une fiche introuvable ne doit pas priver le panier des autres
      }
    }

    if (!products.length) return;

    // Les cartes a plusieurs formats ouvrent la feuille de choix ; celles a
    // format unique renvoient a la fiche. onAdd() et bindVariantChoice() sont
    // conservees, inutilisees, pour pouvoir revenir a l'ajout direct depuis la
    // carte sans les reecrire.
    this.innerHTML = this.template(products);
    this.bindNavigation();
    this.bindFormat();
  }

  /*
    Navigation du carrousel. Sur ordinateur, une souris classique n'a pas d'axe
    horizontal : sans fleches ni glisser, les cartes au-dela de la premiere sont
    inatteignables. Le tactile et le trackpad, eux, savent deja faire, d'ou les
    fleches reservees aux pointeurs fins par le CSS.
  */
  bindNavigation() {
    const liste = this.querySelector('.cart-upsell__list');
    if (!liste) return;

    const pas = () => {
      const carte = liste.querySelector('.cart-upsell__item');
      return carte ? carte.offsetWidth + 10 : 200;
    };

    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    if (prev) prev.addEventListener('click', () => liste.scrollBy({ left: -pas(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => liste.scrollBy({ left: pas(), behavior: 'smooth' }));

    // une fleche qui ne mene nulle part est un piege : on les efface aux bouts
    const majEtat = () => {
      const max = liste.scrollWidth - liste.clientWidth - 1;
      this.classList.toggle('cart-upsell--debut', liste.scrollLeft <= 0);
      this.classList.toggle('cart-upsell--fin', liste.scrollLeft >= max);
      this.classList.toggle('cart-upsell--fixe', max <= 0);
    };
    liste.addEventListener('scroll', majEtat, { passive: true });
    window.addEventListener('resize', majEtat);
    majEtat();

    // glisser a la souris
    let actif = false;
    let bouge = false;
    let departX = 0;
    let departScroll = 0;

    // Sans ceci, appuyer sur une vignette declenche le glisser-deposer natif du
    // navigateur : l'image part en fantome et pointermove ne recoit plus rien.
    liste.addEventListener('dragstart', (event) => event.preventDefault());

    // La molette d'une souris est verticale. Sur ce carrousel on la traduit en
    // defilement horizontal, mais seulement tant qu'il reste de la course :
    // au bout, l'evenement repart au tiroir, qui continue de defiler
    // normalement. Sans cette reserve, la molette serait piegee par le bloc.
    liste.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      const max = liste.scrollWidth - liste.clientWidth;
      if (max <= 0) return;
      if (event.deltaY < 0 && liste.scrollLeft <= 0) return;
      if (event.deltaY > 0 && liste.scrollLeft >= max - 1) return;
      event.preventDefault();
      liste.scrollLeft += event.deltaY;
    }, { passive: false });

    liste.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      actif = true;
      bouge = false;
      departX = event.clientX;
      departScroll = liste.scrollLeft;
      liste.classList.add('is-dragging');
    });

    liste.addEventListener('pointermove', (event) => {
      if (!actif) return;
      const delta = event.clientX - departX;
      if (Math.abs(delta) > 4) bouge = true;
      liste.scrollLeft = departScroll - delta;
    });

    const relacher = () => {
      actif = false;
      liste.classList.remove('is-dragging');
    };
    liste.addEventListener('pointerup', relacher);
    liste.addEventListener('pointerleave', relacher);
    liste.addEventListener('pointercancel', relacher);

    // un glissement ne doit pas suivre le lien de la carte sous le curseur
    liste.addEventListener('click', (event) => {
      if (!bouge) return;
      event.preventDefault();
      event.stopPropagation();
      bouge = false;
    }, true);
  }

  template(products) {
    const heading = this.dataset.heading || '';
    const addLabel = this.dataset.addLabel || '';
    const cards = products
      .map((product) => {
        const available = (product.variants || []).filter((item) => item.available);
        if (!available.length) return '';

        const image = product.featured_image
          ? `<img class="cart-upsell__image" src="${this.sized(product.featured_image, 160)}" alt="" loading="lazy" width="48" height="48">`
          : '<span class="cart-upsell__image cart-upsell__image--empty" aria-hidden="true"></span>';

        // Ni selecteur de format ni prix : la carte invite a ouvrir la fiche, ou
        // le choix se fait avec toute l'information sous les yeux. Le tiroir du
        // panier n'est pas le bon endroit pour arbitrer entre fiole et recharge.
        return `
          <li class="cart-upsell__item">
            <a class="cart-upsell__link" href="${product.url}" tabindex="-1" aria-hidden="true">${image}</a>
            <div class="cart-upsell__details">
              <a class="cart-upsell__title" href="${product.url}">${this.escape(product.title)}</a>
            </div>
            ${
              // Plusieurs formats : on ouvre la feuille de choix sans quitter
              // le tiroir. Un seul format : il n'y a rien a choisir, le lien
              // vers la fiche reste la bonne sortie.
              //
              // Deux actions differentes, deux libelles, mais UN SEUL
              // habillage : cote a cote dans le meme carrousel, un bouton
              // plein et un lien souligne se lisaient comme deux niveaux
              // d'importance alors qu'ils sont l'action de leur carte.
              available.length > 1
                ? `<button type="button" class="cart-upsell__add cart-upsell__add--plein" data-upsell-format data-handle="${this.escape(product.handle)}"><span>${this.escape(this.dataset.formatLabel || 'Choisir un format')}</span><span class="cta-charge__spin" aria-hidden="true"><svg class="spinner" width="20" height="20" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span></button>`
                : `<a class="cart-upsell__add cart-upsell__add--plein" href="${product.url}">${this.escape(addLabel)}</a>`
            }
          </li>`;
      })
      .join('');

    const prev = this.dataset.prevLabel || '';
    const next = this.dataset.nextLabel || '';

    return `
      <div class="cart-upsell__head">
        <p class="cart-upsell__heading">${this.escape(heading)}</p>
        <div class="cart-upsell__nav-group">
          <button type="button" class="cart-upsell__nav cart-upsell__nav--prev" data-upsell-prev aria-label="${this.escape(prev)}"><span class="cart-upsell__chevron"></span></button>
          <button type="button" class="cart-upsell__nav cart-upsell__nav--next" data-upsell-next aria-label="${this.escape(next)}"><span class="cart-upsell__chevron"></span></button>
        </div>
      </div>
      <ul class="cart-upsell__list list-unstyled" role="list">${cards}</ul>`;
  }

  // Ouverture de la feuille de choix. Le controleur vit hors du composant : le
  // tiroir se redessine a chaque ajout au panier et emporterait ses ecouteurs.
  bindFormat() {
    this.querySelectorAll('[data-upsell-format]').forEach((bouton) => {
      bouton.addEventListener('click', (event) => {
        event.preventDefault();
        /* Ce bouton declenche une requete reseau, le rendu de la section de la
           feuille. Sans indicateur, un reseau lent laissait croire au tap
           perdu. La feuille se chargeant de la suite, on relache l'etat des
           qu'elle est montee. */
        bouton.classList.add('cta-charge');
        window.mlsFeuilleFormat.ouvrir(bouton.dataset.handle);
        setTimeout(() => bouton.classList.remove('cta-charge'), 1200);
      });
    });
  }

  // Le prix affiche et la variante ajoutee suivent le format choisi.
  bindVariantChoice() {
    this.querySelectorAll('.cart-upsell__variant').forEach((select) => {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const item = select.closest('.cart-upsell__item');
        const button = item && item.querySelector('[data-upsell-add]');
        const price = item && item.querySelector('.cart-upsell__price');
        if (button) button.dataset.variantId = option.value;
        if (price) price.textContent = this.money.format(Number(option.dataset.price) / 100);
      });
    });
  }

  // featured_image porte deja un ?v=... la plupart du temps, mais pas toujours :
  // choisir le bon separateur sinon l'URL CDN casse silencieusement.
  sized(url, width) {
    if (!url) return '';
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    return `${url}${separator}width=${width}`;
  }

  escape(value) {
    const node = document.createElement('div');
    node.textContent = value == null ? '' : String(value);
    return node.innerHTML;
  }

  async onAdd(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.hasAttribute('disabled')) return;

    button.setAttribute('disabled', 'disabled');
    button.classList.add('cart-upsell__add--loading');

    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];

    try {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();

      if (!response.ok) {
        button.removeAttribute('disabled');
        button.classList.remove('cart-upsell__add--loading');
        return;
      }

      if (drawer && state.sections) {
        drawer.renderContents(state);
      } else {
        window.location.reload();
      }
    } catch (error) {
      button.removeAttribute('disabled');
      button.classList.remove('cart-upsell__add--loading');
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
